import { useQuery as useTanStackQuery, useMutation as useTanStackMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { server_url } from "../Config/API";
import { queryClient } from "../queryClient";

export const useQuery = (urlPath, options = {}) => {
  const queryKey = [urlPath];
  const fullUrl = urlPath && (urlPath.startsWith("http") ? urlPath : `${server_url}${urlPath}`);

  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  const token = localStorage.getItem("careSeeAccessToken");
  if (token) {
    headers["authorization"] = `Bearer ${token}`;
  }

  // Extract React Query options so they don't get passed into the fetch configuration
  const {
    staleTime,
    gcTime,
    enabled = !!urlPath,
    ...fetchOptions
  } = options;

  const { data, isLoading, error, refetch, isFetching } = useTanStackQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        headers,
        signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
    enabled,
    staleTime,
    gcTime,
  });

  return {
    data,
    loading: isLoading, // Using isLoading (first load without cache) instead of isLoading || isFetching to avoid flash-loading spinners on background updates
    error: error ? error.message : null,
    refetch,
    isFetching,
  };
};

export const useMutation = () => {
  const { mutateAsync, isPending, error, data } = useTanStackMutation({
    mutationFn: async ({ urlPath, method = "POST", body = null, headers = {} }) => {
      const fullUrl = urlPath.startsWith("http") ? urlPath : `${server_url}${urlPath}`;
      const defaultHeaders = { ...headers };

      if (!(body instanceof FormData)) {
        defaultHeaders["Content-Type"] = "application/json";
      }

      const token = localStorage.getItem("careSeeAccessToken");
      if (token) {
        defaultHeaders["authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(fullUrl, {
        method,
        headers: defaultHeaders,
        body: body instanceof FormData ? body : body ? JSON.stringify(body) : null,
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || `HTTP error! status: ${response.status}`);
      }

      return result;
    },
    onSuccess: () => {
      // Invalidate all queries on any successful mutation to auto-refresh all data active on page
      queryClient.invalidateQueries();
    },
  });

  const mutate = useCallback(
    async (urlPath, mutationOptions = {}) => {
      return mutateAsync({ urlPath, ...mutationOptions });
    },
    [mutateAsync]
  );

  return {
    mutate,
    loading: isPending,
    error: error ? error.message : null,
    data,
  };
};
